import { Component, NgZone, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { GoogleMap, GoogleMapsModule } from '@angular/google-maps';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { DropdownModule } from 'primeng/dropdown';
import { BadgeModule } from 'primeng/badge';
import { ConnectionStatus, TrackingSignalrService } from '../../core/service/tracking-signalr.service';
import { CoreApiService } from '../../core/service/core-api-service';
import { AppService } from '../../core/service/app.service';
import { PoliticalDivisionModel } from '../../model/political-division.model';
import { properties } from '../../../environments/properties';
import { Subscription } from 'rxjs';

interface DriverMarkerState {
  marker: google.maps.marker.AdvancedMarkerElement;
  element: HTMLElement;
  currentLat: number;
  currentLng: number;
  startLat: number;
  startLng: number;
  targetLat: number;
  targetLng: number;
  animStart: number;
  heading: number;
  rafId: number | null;
}

const ANIM_MS = 1100;

@Component({
  selector: 'app-mapa',
  standalone: true,
  imports: [GoogleMapsModule, CommonModule, FormsModule, DropdownModule, BadgeModule],
  templateUrl: './mapa.component.html',
  styleUrl: './mapa.component.css',
})
export class MapaComponent implements OnInit, OnDestroy {
  @ViewChild(GoogleMap) googleMap!: GoogleMap;

  options: google.maps.MapOptions = {
    mapId: 'DEMO_MAP_ID',
    zoomControl: true,
    disableDoubleClickZoom: true,
  };

  defaultCenter: google.maps.LatLngLiteral = { lat: -2.8978, lng: -79.0045 };
  center: google.maps.LatLngLiteral = this.defaultCenter;
  zoom = 14;

  public citiesList: PoliticalDivisionModel[] = [];
  public selectedCity: PoliticalDivisionModel | null = null;
  public selectedDriver: any = null;
  public connectionStatus: ConnectionStatus = 'disconnected';
  public isRetrying = false;
  private currentCityCode = '';
  private isConnected = false;

  private readonly activeDriversMap = new Map<string, any>();
  private readonly markerStateMap = new Map<string, DriverMarkerState>();
  private AdvancedMarkerElement?: typeof google.maps.marker.AdvancedMarkerElement;
  private infoWindowNative?: google.maps.InfoWindow;
  public driversList: any[] = [];
  private readonly subs: Subscription = new Subscription();
  public companyId: number = 0;
  public stateId: number = 0 ;

  constructor(
    private readonly trackingService: TrackingSignalrService,
    private readonly ngZone: NgZone,
    private readonly util: AppService,
    private readonly apiService: CoreApiService,
  ) {}

  ngOnInit(): void {
    this.companyId = Number(this.util.returnCompany());
    this.stateId = Number(this.util.returnStateId());
    this.initMarkerLibrary();
    this.loadCities();
    this.trackingService.startConnection(this.util.returnToken());

    this.subs.add(
      this.trackingService.connectionReady$.subscribe(() => {
        this.isConnected = true;
        if (this.citiesList.length > 0) this.autoJoinFirstCity();
      })
    );

    this.subs.add(
      this.trackingService.initialDrivers$.subscribe((driversArray: any[]) => {
        this.ngZone.run(() => {
          if (driversArray?.length > 0) {
            this.center = { lat: driversArray[0].latitude, lng: driversArray[0].longitude };
            driversArray.forEach(driver => {
              this.activeDriversMap.set(driver.driverId, driver);
              this.placeOrUpdateMarker(driver, false);
            });
          } else {
            this.center = this.defaultCenter;
          }
          this.updateViewArray();
        });
      })
    );

    this.subs.add(
      this.trackingService.driverMoved$.subscribe(driver => {
        this.ngZone.run(() => {
          const existing = this.activeDriversMap.get(driver.driverId) ?? {};
          const merged = { ...existing, ...driver };
          this.activeDriversMap.set(driver.driverId, merged);
          this.placeOrUpdateMarker(merged, true);
          this.updateViewArray();
        });
      })
    );

    this.subs.add(
      this.trackingService.clearDrivers$.subscribe(() => {
        this.ngZone.run(() => {
          this.clearAllMarkers();
          this.activeDriversMap.clear();
          this.driversList = [];
          this.center = this.defaultCenter;
          this.selectedDriver = null;
        });
      })
    );

    this.subs.add(
      this.trackingService.connectionStatus$.subscribe(status => {
        this.ngZone.run(() => (this.connectionStatus = status));
      })
    );

    this.subs.add(
      this.trackingService.isRetrying$.subscribe(retrying => {
        this.ngZone.run(() => (this.isRetrying = retrying));
      })
    );

    this.subs.add(
      this.trackingService.reconnected$.subscribe(() => {
        this.ngZone.run(() => {
          // Limpiar estado local: los grupos de SignalR se perdieron en el servidor
          this.clearAllMarkers();
          this.activeDriversMap.clear();
          this.driversList = [];
          this.selectedDriver = null;
          // Volver a unirse a la ciudad actual → el servidor envía InitialDriversLoad
          if (this.currentCityCode) {
            this.trackingService.joinAdminDashboard(this.currentCityCode);
          }
        });
      })
    );
  }

  private async initMarkerLibrary(): Promise<void> {
    const lib = await google.maps.importLibrary('marker') as google.maps.MarkerLibrary;
    this.AdvancedMarkerElement = lib.AdvancedMarkerElement;
  }

  private buildCarElement(): HTMLElement {
    const div = document.createElement('div');
    div.className = 'car-marker';

    const halo = document.createElement('div');
    halo.className = 'car-marker-halo';
    div.appendChild(halo);

    const img = document.createElement('img');
    img.src = 'assets/images/car-icon.png';
    img.style.cssText = 'width:100%;height:100%;display:block;pointer-events:none;position:relative;z-index:2;';
    div.appendChild(img);
    return div;
  }

  private placeOrUpdateMarker(driver: any, animate: boolean): void {
    if (!this.AdvancedMarkerElement) return;
    const map = this.googleMap?.googleMap;
    if (!map) return;

    const existing = this.markerStateMap.get(driver.driverId);

    if (!existing) {
      const element = this.buildCarElement();
      const marker = new this.AdvancedMarkerElement({
        map,
        position: { lat: driver.latitude, lng: driver.longitude },
        content: element,
        title: driver.driverName
          ? `${driver.driverName} ${driver.driverLastName}`
          : driver.driverId,
      });

      marker.addListener('click', () => {
        this.ngZone.run(() => {
          const d = this.activeDriversMap.get(driver.driverId);
          this.selectedDriver = d;
          this.highlightMarker(driver.driverId);
        });
      });

      this.markerStateMap.set(driver.driverId, {
        marker,
        element,
        currentLat: driver.latitude,
        currentLng: driver.longitude,
        startLat: driver.latitude,
        startLng: driver.longitude,
        targetLat: driver.latitude,
        targetLng: driver.longitude,
        animStart: 0,
        heading: 0,
        rafId: null,
      });
      return;
    }

    const heading = this.calculateBearing(
      { lat: existing.currentLat, lng: existing.currentLng },
      { lat: driver.latitude, lng: driver.longitude }
    );

    if (Math.abs(heading - existing.heading) > 3) {
      existing.element.style.transform = `rotate(${heading - 90}deg)`;
      existing.heading = heading;
    }

    existing.startLat = existing.currentLat;
    existing.startLng = existing.currentLng;
    existing.targetLat = driver.latitude;
    existing.targetLng = driver.longitude;
    existing.animStart = performance.now();

    if (animate) {
      if (existing.rafId !== null) {
        cancelAnimationFrame(existing.rafId);
        existing.rafId = null;
      }
      this.startAnimation(driver.driverId);
    } else {
      existing.currentLat = driver.latitude;
      existing.currentLng = driver.longitude;
      existing.marker.position = { lat: driver.latitude, lng: driver.longitude };
    }
  }

  private startAnimation(driverId: string): void {
    this.ngZone.runOutsideAngular(() => {
      const step = (now: number) => {
        const state = this.markerStateMap.get(driverId);
        if (!state) return;

        const t = Math.min((now - state.animStart) / ANIM_MS, 1);
        const eased = t < 0.5 ? 2 * t * t : -1 + (4 - 2 * t) * t;

        state.currentLat = state.startLat + (state.targetLat - state.startLat) * eased;
        state.currentLng = state.startLng + (state.targetLng - state.startLng) * eased;
        state.marker.position = { lat: state.currentLat, lng: state.currentLng };

        if (t < 1) {
          state.rafId = requestAnimationFrame(step);
        } else {
          state.rafId = null;
        }
      };

      const state = this.markerStateMap.get(driverId);
      if (state) state.rafId = requestAnimationFrame(step);
    });
  }

  private openNativeInfoWindow(anchor: google.maps.marker.AdvancedMarkerElement): void {
    if (!this.infoWindowNative) {
      this.infoWindowNative = new google.maps.InfoWindow();
    }
    const d = this.selectedDriver;
    const name = d?.driverName ? `${d.driverName} ${d.driverLastName}` : d?.driverId ?? '';
    const plate = d?.driverPlate
      ? `<div class="iw-plate"><span class="pi pi-car"></span> ${d.driverPlate}</div>`
      : '';
    const address = d?.driverAddress
      ? `<div class="iw-address"><span class="pi pi-home"></span> ${d.driverAddress}</div>`
      : '';
    const company = d?.driverCompany
      ? `<div class="iw-company">${d.driverCompany}</div>`
      : '';
    const photo = d?.driverUrl
      ? `<img src="${d.driverUrl}" alt="${name}" class="iw-photo" onerror="this.src='assets/images/default-driver.png'" />`
      : `<img src="assets/images/default-driver.png" alt="${name}" class="iw-photo" />`;
    this.infoWindowNative.setContent(
      `<div class="info-window-marker">
        <div class="iw-container">
          <div class="iw-photo-wrapper">${photo}</div>
          <div class="iw-details">
            <div class="iw-name">${name}</div>
            ${plate}${address}${company}
          </div>
        </div>
      </div>`
    );
    this.infoWindowNative.open({
      map: this.googleMap.googleMap!,
      anchor,
    });
  }

  private calculateBearing(
    from: google.maps.LatLngLiteral,
    to: google.maps.LatLngLiteral
  ): number {
    const lat1 = (from.lat * Math.PI) / 180;
    const lat2 = (to.lat * Math.PI) / 180;
    const dLng = ((to.lng - from.lng) * Math.PI) / 180;
    const y = Math.sin(dLng) * Math.cos(lat2);
    const x =
      Math.cos(lat1) * Math.sin(lat2) -
      Math.sin(lat1) * Math.cos(lat2) * Math.cos(dLng);
    return ((Math.atan2(y, x) * 180) / Math.PI + 360) % 360;
  }

  private clearAllMarkers(): void {
    this.markerStateMap.forEach(state => {
      if (state.rafId !== null) cancelAnimationFrame(state.rafId);
      state.marker.map = null;
    });
    this.markerStateMap.clear();
    this.infoWindowNative?.close();
  }

  private loadCities(): void {
    this.apiService.consumirWSGet(
      properties.servicePoliticalDivisions,
      null,
      (data) => {
        if (data.estado === properties.codeOK) {
          this.citiesList = (data.data as PoliticalDivisionModel[])
            .filter(pd => pd.pd_type === 2 && pd.pd_active);
          if(this.companyId != 0 ){
            this.citiesList = this.citiesList.filter(pd => pd.pd_code === this.stateId && pd.pd_active);
          }
          if (this.citiesList.length > 0 && this.isConnected) {
            this.autoJoinFirstCity();
          }
        } else {
          this.util.mostrarToast('Mapa', 'Error al cargar ciudades', 4);
        }
      }
    );
  }

  private autoJoinFirstCity(): void {
    this.selectedCity = this.citiesList[0];
    this.defaultCenter = { lat: this.selectedCity.pd_latitude, lng: this.selectedCity.pd_longitude };
    this.currentCityCode = this.citiesList[0].pd_code.toString();
    this.trackingService.joinAdminDashboard(this.currentCityCode);
  }

  public onCityChange(city: PoliticalDivisionModel): void {
    const newCityCode = city.pd_code.toString();
    if (newCityCode === this.currentCityCode) return;
    this.trackingService.switchCity(this.currentCityCode, newCityCode);
    this.currentCityCode = newCityCode;
    this.selectedCity = city;
    this.defaultCenter = { lat: this.selectedCity.pd_latitude, lng: this.selectedCity.pd_longitude };
  }

  public retryConnection(): void {
    this.trackingService.retry(this.util.returnToken());
  }

  public centerOnDriver(driver: any): void {
    this.selectedDriver = driver;
    this.ngZone.run(() => {
      this.center = { lat: driver.latitude, lng: driver.longitude };
      this.zoom = 16;
      this.highlightMarker(driver.driverId);
    });
  }

  private highlightMarker(driverId: string | null): void {
    // 1. Remove highlight class and reset zIndex
    this.markerStateMap.forEach((state) => {
      state.element.classList.remove('car-marker--selected');
      state.marker.zIndex = 1;
    });

    // 2. Add selection highlight to the specific driver's marker
    if (driverId) {
      const state = this.markerStateMap.get(driverId);
      if (state) {
        state.element.classList.add('car-marker--selected');
        state.marker.zIndex = 9999;
        this.openNativeInfoWindow(state.marker);
      }
    }
  }

  private updateViewArray(): void {
    this.driversList = Array.from(this.activeDriversMap.values());
  }

  ngOnDestroy(): void {
    this.clearAllMarkers();
    this.trackingService.leaveAdminDashboard(this.currentCityCode);
    this.subs.unsubscribe();
    this.trackingService.stopConnection();
  }
}
